/**
 * DBMS 카탈로그 그리드의 **조회 쿼리 조립** — 화면에서 누른 것이 그대로 SQL 로 보인다.
 *
 * 왜 따로 두는가: 이 화면은 "무엇을 조회했는지" 를 SQL 로 보여 주는 것이 핵심이다.
 * 값을 눌러 거른 조건도, 머리글을 눌러 건 정렬도 **쿼리 본문에 나타나야** 사용자가
 * 그 결과를 믿고 복사해 쓸 수 있다. 조립 규칙이 화면 코드에 흩어지면 화면과 SQL 이
 * 어긋나는 순간이 온다.
 *
 * **사용자 입력을 SQL 에 그대로 붙이는 유일한 자리**이므로 여기서 다 막는다:
 * 식별자는 화이트리스트 정규식, 값은 작은따옴표 이스케이프. 바인드를 쓰지 않는 이유는
 * 이 SQL 이 **사용자에게 보여지고 손으로 고칠 수 있는 글**이기 때문이다 — `:b1` 이 박힌
 * 쿼리는 복사해서 다른 도구에 붙일 수가 없다.
 */

/** 식별자(뷰·컬럼) — 영문/숫자/밑줄/$/# 과 점 하나까지. Oracle·PostgreSQL 공통으로 충분하다 */
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_$#]*(\.[A-Za-z_][A-Za-z0-9_$#]*)?$/;

function isIdent(s) {
    return typeof s === 'string' && IDENT_RE.test(s);
}

/**
 * 리터럴로 굳힌다. 숫자는 그대로, 나머지는 작은따옴표 문자열.
 * `null` 은 값이 아니라 **조건의 종류**가 달라지므로(`IS NULL`) 따로 다룬다.
 */
function literal(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    // 숫자로 보이는 문자열도 숫자 컬럼일 수 있지만 **따옴표를 씌운다** —
    // Oracle 은 문자 리터럴을 숫자 컬럼에 암시적 변환해 주지만 그 반대는 아니다.
    // 틀리면 조용히 다른 계획으로 도는 것보다 눈에 띄게 실패하는 편이 낫다.
    return `'${String(v).replace(/'/g, "''")}'`;
}

/** 조건 하나를 SQL 조각으로. 못 만들면 null(=무시) */
function predicate(f) {
    if (!f || !isIdent(f.col)) return null;
    if (f.value === null || f.value === undefined) return `${f.col} IS NULL`;
    const lit = literal(f.value);
    return lit == null ? null : `${f.col} = ${lit}`;
}

/**
 * @param {object} opt
 * @param {string} opt.viewName
 * @param {Array<{col: string, value: *}>} [opt.filters]  값 클릭으로 쌓인 조건(AND)
 * @param {{col: string, dir: 'asc'|'desc'}|null} [opt.sort]
 * @param {number|null} [opt.instId]   GV$ 뷰에서 특정 인스턴스만 볼 때
 * @returns {string}
 */
function buildCatalogQuery({ viewName, filters = [], sort = null, instId = null } = {}) {
    if (!isIdent(viewName)) return null;

    const where = [];
    // 인스턴스 필터를 **맨 앞에** 둔다 — GV$ 뷰에서 가장 먼저 읽는 조건이라 눈에 띄어야 한다.
    if (instId != null && Number.isFinite(Number(instId))) {
        where.push(`inst_id = ${Number(instId)}`);
    }
    filters.forEach((f) => {
        const p = predicate(f);
        if (p) where.push(p);
    });

    let sql = `SELECT * FROM ${viewName}`;
    if (where.length > 0) sql += `${'\n'} WHERE ${where.join(`${'\n'}   AND `)}`;
    if (sort && isIdent(sort.col) && (sort.dir === 'asc' || sort.dir === 'desc')) {
        sql += `${'\n'} ORDER BY ${sort.col} ${sort.dir === 'desc' ? 'DESC' : 'ASC'}`;
    }
    return sql;
}

/** 머리글 클릭 토글: 순방향 → 역방향 → 해제 */
function nextSort(cur, col) {
    if (!cur || cur.col !== col) return { col, dir: 'asc' };
    if (cur.dir === 'asc') return { col, dir: 'desc' };
    return null;
}

/** 같은 컬럼·같은 값을 두 번 걸지 않는다 — 조건이 중복되면 리본만 지저분해진다 */
function addFilter(filters, col, value) {
    const list = Array.isArray(filters) ? filters : [];
    if (!isIdent(col)) return list;
    const same = (f) => f.col === col && ((f.value == null && value == null) || String(f.value) === String(value));
    if (list.some(same)) return list;
    return [...list, { col, value: value === undefined ? null : value }];
}

/** GV$ 뷰인가 — 인스턴스 리본을 세울지 판단한다 */
function isGlobalView(viewName) {
    return typeof viewName === 'string' && /^GV\$/i.test(viewName.trim());
}

export {
    buildCatalogQuery,
    nextSort,
    addFilter,
    isGlobalView,
    isIdent,
    literal,
    predicate,
};
